import * as THREE from 'three';
import { MeshMatcapNodeMaterial } from 'three/webgpu';

import { EngineClass } from '../../../systems/ClassRegistry.js';
import { property } from '../../../utils/serialization/decorator.js';

import { NodeMaterialAsset } from './NodeMaterialAsset.js';

import type { TexturePath } from '../../../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../../../utils/serialization/factories.js';

/** WebGPU `MeshMatcapNodeMaterial` asset: matcap lighting with optional tint. */
@EngineClass('MeshMatcapNodeMaterialAsset', {
  isNodeMaterialAsset: true,
  nodeMaterialDisplayName: 'Mesh Matcap Node Material',
  nodeMaterialGroup: 'TSL Node (THREE)',
})
export class MeshMatcapNodeMaterialAsset extends NodeMaterialAsset(MeshMatcapNodeMaterial) {

  @property({ type: 'color', description: 'Tint color' })
  override color = new THREE.Color(1.0, 1.0, 1.0);

  @property({ type: 'boolean', description: 'Enable flat shading' })
  override flatShading = false;

  @property({ type: 'number', min: -10, max: 10, step: 0.01, description: 'Bump map influence' })
  override bumpScale = 1.0;

  @property({ type: 'vector2', description: 'Normal map influence' })
  override normalScale = new THREE.Vector2(1, 1);

  @property({ type: 'number', description: 'Normal map type (`THREE.TangentSpaceNormalMap`, `ObjectSpaceNormalMap`)' })
  override normalMapType = THREE.TangentSpaceNormalMap;

  @property({ type: 'texturePath', description: 'Color map path' })
    mapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Matcap map path' })
    matcapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Alpha map path' })
    alphaMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Bump map path' })
    bumpMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Normal map path' })
    normalMapPath: TexturePath = '';

  @property({ type: 'texturePath', description: 'Displacement map path' })
    displacementMapPath: TexturePath = '';

  constructor() {
    super();
    this.rebuild();
  }

  override rebuild(): void {
    this.applyCommonMaterialState();
    this.bumpScale = this.clampFiniteNumber(this.bumpScale, -10, 10, 1.0);
    this.syncTexturePath('map', this.mapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('matcap', this.matcapPath, THREE.SRGBColorSpace);
    this.syncTexturePath('alphaMap', this.alphaMapPath, THREE.NoColorSpace);
    this.syncTexturePath('bumpMap', this.bumpMapPath, THREE.NoColorSpace);
    this.syncTexturePath('normalMap', this.normalMapPath, THREE.NoColorSpace);
    this.syncTexturePath('displacementMap', this.displacementMapPath, THREE.NoColorSpace);
    this.colorNode = null;
  }

  public serialize(dumper: IDumper): void {
    this.serializeAuthoredFields(dumper);
  }

  public static staticDeserialize(data: unknown, loader: ILoader): MeshMatcapNodeMaterialAsset {
    const instance = new MeshMatcapNodeMaterialAsset();
    MeshMatcapNodeMaterialAsset.loadAuthoredFields(instance, loader);
    instance.rebuild();
    return instance;
  }
}
